import { Dialog as ADialog, useDialogContext } from '@ark-ui/solid/dialog';
import { createVisibilityObserver } from '@solid-primitives/intersection-observer';
import { children, createContext, createSignal, Show, splitProps, useContext } from 'solid-js';
import { Portal } from 'solid-js/web';
import { IconCloseMd } from '@troon/icons/close-md';
import { twMerge } from '@troon/tailwind-preset/merge';
import { Button } from '../forms/button';
import { Heading } from '../components/text';
import { useUIContext } from '../providers/global';
import { Form } from '../forms/form';
import type { Accessor, ComponentProps, JSXElement } from 'solid-js';

export { useDialogContext } from '@ark-ui/solid/dialog';

export function Dialog(props: ComponentProps<typeof ADialog.Root> & { key: string }) {
	const ctx = useUIContext();
	return (
		<ADialog.Root
			lazyMount
			preventScroll
			{...props}
			onOpenChange={(open) => {
				props.onOpenChange?.(open);
				if (open) {
					ctx.onDialogOpened?.(props.key);
				} else if (!open) {
					ctx.onDialogClosed?.(props.key);
				}
			}}
		>
			{props.children}
		</ADialog.Root>
	);
}

function DialogTrigger(props: ComponentProps<typeof ADialog.Trigger>) {
	return <ADialog.Trigger {...props} />;
}
Dialog.Trigger = DialogTrigger;

function DialogButtonTrigger(props: ComponentProps<typeof Button>) {
	return <ADialog.Trigger asChild={(p) => <Button {...p()} {...props} />} />;
}
Dialog.ButtonTrigger = DialogButtonTrigger;

const Context = createContext<{ root: Accessor<HTMLElement | undefined> }>({
	root: () => undefined,
});

type ContentProps = ComponentProps<typeof ADialog.Content> & {
	noBackdrop?: boolean;
	desktopWidth?: 'sm' | 'md' | 'lg' | 'fit';
	mobileHeight?: 'fit' | 'full';
};

function DialogContainer(props: ContentProps) {
	const [internal, spread] = splitProps(props, ['children', 'desktopWidth', 'mobileHeight']);
	const [ref, setRef] = createSignal<HTMLElement>();

	return (
		<Portal>
			<ADialog.Positioner class="pointer-events-none fixed inset-0 z-40 flex sm:items-center sm:justify-center sm:p-8">
				<Show when={!props.noBackdrop}>
					<ADialog.Backdrop class="pointer-events-auto fixed inset-0 z-40 bg-black/50 backdrop-blur-sm data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:animate-in data-[state=open]:fade-in" />
				</Show>
				<ADialog.Content
					{...spread}
					ref={setRef}
					class="pointer-events-auto fixed bottom-0 z-50 flex w-dvw max-w-full flex-col overflow-x-hidden overflow-y-auto overscroll-contain rounded-t-md border border-neutral bg-white text-neutral-950 shadow-xl duration-150 outline-none data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:slide-out-to-bottom data-[state=open]:animate-in data-[state=open]:duration-200 data-[state=open]:fade-in data-[state=open]:slide-in-from-bottom sm:bottom-auto sm:my-auto sm:h-fit sm:max-h-[min(44rem,95dvh)] sm:w-full sm:rounded-lg"
					classList={{
						'sm:max-w-xl': internal.desktopWidth === 'sm',
						'sm:max-w-2xl': internal.desktopWidth === 'md',
						'sm:max-w-4xl': internal.desktopWidth === 'lg',
						'sm:max-w-max': !internal.desktopWidth || internal.desktopWidth === 'fit',
						'max-h-[97dvh]': !internal.mobileHeight || internal.mobileHeight === 'fit',
						'h-[97dvh]': internal.mobileHeight === 'full',
					}}
				>
					<Show when={ref()}>
						{(ref) => <Context.Provider value={{ root: ref }}>{props.children}</Context.Provider>}
					</Show>
				</ADialog.Content>
			</ADialog.Positioner>
		</Portal>
	);
}
Dialog.Container = DialogContainer;

function DialogCloseTrigger(props: ComponentProps<typeof ADialog.CloseTrigger>) {
	const ctx = useDialogContext();
	const child = children(() => props.children);
	return (
		<ADialog.CloseTrigger
			asChild={(p) => (
				<Button {...p()} appearance="transparent" size="sm" onClick={() => ctx().setOpen(false)} tabindex={1} />
			)}
		>
			<Show when={child()} fallback={<IconCloseMd class="size-5" />}>
				{(children) => children()}
			</Show>
			<span class="sr-only">Close</span>
		</ADialog.CloseTrigger>
	);
}
Dialog.CloseTrigger = DialogCloseTrigger;

function DialogTitle(props: ComponentProps<typeof ADialog.Title> & { noClose?: boolean }) {
	return (
		<div class="sticky top-0 z-10 flex h-16 shrink-0 grow-0 flex-row items-center justify-between gap-4 border-b border-neutral bg-white py-2 ps-3 pe-0 transition-colors sm:ps-6">
			<ADialog.Title asChild={(p) => <Heading as="h1" size="h6" {...p} />}>{props.children}</ADialog.Title>
			<Show when={!props.noClose}>
				<div class="ms-auto shrink pe-1 sm:pe-3">
					<DialogCloseTrigger />
				</div>
			</Show>
		</div>
	);
}
Dialog.Title = DialogTitle;

Dialog.Form = (props: ComponentProps<typeof Form>) => <Form {...props} class="gap-y-0" />;

type CloseRenderChildren = (handleClose: () => void) => JSXElement;

function DialogContent(props: { children: JSXElement | CloseRenderChildren }) {
	const ctx = useDialogContext();
	const resolved = children(
		() =>
			// @ts-expect-error this is technically correct: JSXElement children can be a function
			props.children,
	);
	return (
		<div class="size-full self-start px-3 py-3 sm:px-6">
			{typeof resolved() === 'function'
				? // @ts-expect-error this is correct per the narrowing
					resolved()(() => ctx().setOpen(false))
				: resolved()}
		</div>
	);
}
Dialog.Content = DialogContent;

function DialogActions(props: { children: JSXElement | CloseRenderChildren; class?: string }) {
	const ctx = useDialogContext();
	const resolved = children(
		() =>
			// @ts-expect-error this is technically correct: JSXElement children can be a function
			props.children,
	);
	const { root } = useContext(Context);
	const [ref, setRef] = createSignal<HTMLElement>();
	const observer = createVisibilityObserver({ root: root(), threshold: 1 });
	// eslint-disable-next-line solid/reactivity
	const visible = observer(ref);

	return (
		<>
			<div
				class={twMerge(
					'sticky bottom-0 z-10 flex shrink-0 grow-0 flex-row-reverse flex-wrap items-stretch justify-start gap-4 border-t bg-white px-3 py-3 sm:items-center sm:px-6 sm:pb-6',
					props.class,
				)}
				classList={{
					'border-neutral': !visible(),
					'border-white': visible(),
				}}
			>
				{typeof resolved() === 'function'
					? // @ts-expect-error this is correct per the narrowing
						resolved()(() => ctx().setOpen(false))
					: resolved()}
			</div>
			<div ref={setRef} />
		</>
	);
}
Dialog.Actions = DialogActions;

function DialogAction(props: ComponentProps<typeof Button>) {
	return <Button class="basis-full sm:size-fit sm:grow-0 sm:basis-auto" {...props} />;
}
Dialog.Action = DialogAction;
